import { FileUp, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useFilePicker } from '@/hooks/use-file-picker'
import { useImportCampaignCsv } from '@/queries'

/** Subir un CSV y ver qué filas valen, y por qué no valen las demás. */
export function CsvImport({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation()
  const upload = useImportCampaignCsv(campaignId)
  const { open, picker } = useFilePicker(
    (file) => upload.mutate(file),
    t('campaigns.csv.pick'),
    '.csv,text/csv',
  )
  const report = upload.data

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {picker}
        <Button type="button" variant="outline" disabled={upload.isPending} onClick={open}>
          {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
          {t('campaigns.csv.pick')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('campaigns.csv.help')}</p>
      </div>

      {upload.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{upload.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {report ? (
        <div className="space-y-2">
          <p className="text-sm" data-testid="csv-summary">
            {t('campaigns.csv.summary', {
              valid: report.validas,
              invalid: report.invalidas.length,
              duplicates: report.duplicadas,
            })}
          </p>
          {report.invalidas.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">{t('campaigns.csv.col_row')}</TableHead>
                    <TableHead>{t('campaigns.csv.col_value')}</TableHead>
                    <TableHead>{t('campaigns.csv.col_reason')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.invalidas.map((row) => (
                    <TableRow key={row.fila}>
                      <TableCell className="text-right tabular-nums">{row.fila}</TableCell>
                      <TableCell className="font-mono">{row.valor}</TableCell>
                      <TableCell>{t(`campaigns.csv.reason.${row.motivo}`, { defaultValue: row.motivo })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
